/**
 * F6 · নিরাপত্তা — `/account/security`
 *
 * Transcribed from `design/Oprappo P0 Screens.dc.html`, frame F6 (360 × 680).
 *
 * Frame note: "Each toggle says what it protects against in plain terms. Device removal
 * is one tap from the device it concerns — a shared-phone market needs that within
 * reach, not buried in settings."
 *
 * WIRED. `GET /v1/account/security` supplies the title, both toggles with the sentence
 * each gives for itself, the device list and the login-history block. `PATCH` writes a
 * toggle and `DELETE …/sessions?id=` is the drawn `বের করে দিন`; both answer with the
 * whole screen, which is what re-renders.
 *
 * THERE IS NO "SIGN OUT EVERYWHERE" BUTTON HERE, and its absence is deliberate. The
 * capability exists — `POST /v1/account/security/sign-out-all` — and F6 draws no control
 * for it and no label to put on one. Rendering a button would mean writing the Bangla for
 * it, which docs/01 § prohibited forbids; the sentence is recorded in
 * `docs/bangla-needed/account-activity.md` instead.
 *
 * THE CURRENT DEVICE HAS NO REMOVAL CONTROL, and the route refuses one even if asked
 * directly (`account/sql.ts § DELETE_SESSION`). The screen is not the boundary.
 *
 * The switch is drawn only here: `44 × 26`, `border-radius:999px`, teal when on and
 * `#E2DED5` when off, with a 20px white knob and `padding:0 3px`. It is not in
 * `@oprappo/ui` — the design system draws no switch — so it is transcribed and reported
 * as a missing component rather than added to a package WP-D does not own.
 */
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@oprappo/ui';
import type { SecurityResponse } from '@oprappo/contracts';
import { Frame, SAND_FRAME, ScreenHeader } from '../offering/chrome.js';
import { SurfaceStates } from '../../states/boundary.js';
import { useResource } from '../../data/resource.js';
import { fetchSecurity, removeDevice, setSecurityToggle } from './api.js';

function Switch({ on, labelBn }: { on: boolean; labelBn: string }): JSX.Element {
  return (
    <span
      role="switch"
      aria-checked={on}
      aria-label={labelBn}
      className={
        'w-[44px] h-[26px] rounded-pill flex items-center px-[3px] flex-none ' +
        (on ? 'bg-teal justify-end' : 'bg-border justify-start')
      }
    >
      <span className="w-[20px] h-[20px] bg-white rounded-full" />
    </span>
  );
}

/**
 * THE SCREEN IS A PURE FUNCTION OF THE PAYLOAD.
 *
 * The view takes what the route returned and renders it; the container below does the
 * loading. That split is what lets the fidelity tests render this screen from the same
 * `fixtures/api/*.json` file the API's own suite asserts its response against, so the
 * chain the visual gate depends on is checked end to end: drawn frame == fixture == API
 * output == this markup.
 *
 * `null` means "nothing has arrived yet", and the view renders its chrome and no content
 * rather than substituting design data.
 */
export function F6SecurityView({ data }: { data: SecurityResponse | null }): JSX.Element {
  const navigate = useNavigate();

  // Every write answers with the whole screen, and that answer is what renders. A locally
  // flipped switch is a switch that says "on" while the server has it off.
  const [saved, setSaved] = useState<SecurityResponse | null>(null);
  const s = saved ?? data;

  return (
    <Frame className={SAND_FRAME}>
      <ScreenHeader titleBn={s?.title_bn ?? ''} onBack={() => navigate('/account')} />

      <div className="flex-1 p-[16px] oprappo-scroll">
        {(s?.toggles ?? []).map((t, i) => (
          <button
            key={t.id}
            type="button"
            onClick={() => void setSecurityToggle({ id: t.id, on: !t.on }).then(setSaved)}
            className={
              'w-full bg-white border border-border p-[14px] flex justify-between items-center gap-[12px] rounded-none text-left' +
              (i === 0 ? '' : ' mt-[11px]')
            }
          >
            <span className="flex-1">
              <span className="block font-anek font-medium text-[15.5px] leading-[1.75] text-ink">
                {t.label_bn}
              </span>
              <span className="block font-anek font-normal text-[13.5px] leading-[1.75] text-muted mt-[2px]">
                {t.detail_bn}
              </span>
            </span>
            <Switch on={t.on} labelBn={t.label_bn} />
          </button>
        ))}

        <div className="font-anek font-semibold text-[15px] leading-[1.75] text-muted mt-[22px] mb-[11px]">
          {s?.devices_heading_bn ?? ''}
        </div>
        {(s?.devices ?? []).map((d, i) => (
          <div
            key={d.id}
            className={'bg-white border border-border p-[14px]' + (i === 0 ? '' : ' mt-[9px]')}
          >
            <div className="flex justify-between items-start gap-[11px]">
              <div className="flex-1">
                <div className="font-anek font-medium text-[15.5px] leading-[1.75] text-ink">
                  {d.label_bn}
                </div>
                <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-muted mt-[2px]">
                  {d.detail_bn}
                </div>
              </div>
              {/*
                * Exactly one of the two, and WHICH is the server's decision: the device
                * you are holding carries the `এখন` badge and no control, every other one
                * carries the control and no badge. `deviceListIsWellFormed` in
                * `@oprappo/contracts` is asserted on the way out, so a list that cannot
                * say which device you are on never reaches this screen.
                */}
              {d.badge_bn !== null ? (
                <Badge labelBn={d.badge_bn} tone="success" dense className="flex-none" />
              ) : d.remove_bn !== null ? (
                <button
                  type="button"
                  onClick={() => void removeDevice(d.id).then(setSaved)}
                  className="font-anek font-medium text-[13.5px] leading-[1.75] text-danger flex-none bg-transparent border-0 p-0 rounded-none"
                >
                  {d.remove_bn}
                </button>
              ) : null}
            </div>
          </div>
        ))}

        <div className="border-t border-border mt-[20px] pt-[16px]">
          <div className="font-anek font-medium text-[15.5px] leading-[1.75] text-ink2">
            {s?.history_title_bn ?? ''}
          </div>
          <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-muted mt-[3px]">
            {s?.history_body_bn ?? ''}
          </div>
        </div>
      </div>
    </Frame>
  );
}

export function F6Security(): JSX.Element {
  const load = useCallback((signal: AbortSignal) => fetchSecurity(signal), []);
  const { data, error } = useResource(load, 'security');

  return (
    <SurfaceStates surface="account" error={error ?? undefined}>
      <F6SecurityView data={data} />
    </SurfaceStates>
  );
}
